"use client";

import { useEffect, useRef, useState } from "react";
import type React from "react";
import { createPortal } from "react-dom";
import { Check, Play, X } from "lucide-react";

import { useAnalytics } from "@/components/analytics/AnalyticsProvider";
import { EVENTS } from "@/lib/analytics/events";
import { loadWatched, markWatched, useWatchedFilms } from "@/components/copilot/onboardingProgress";
import { useTranslation } from "react-i18next";
import { UI_LANGUAGES, msg } from "@/lib/i18n";

// The onboarding films, recorded from the real portal (video/onboarding-journey and
// video/onboarding-schedule-interview) and served from this site like the home film:
// the files carry a version and nginx caches /film/ for a year, so a new cut needs a
// new version. They play in this order; the intro says what the others are.
export const FILMS = [
  { id: "intro", title: msg("What's in these films"), label: msg("Start here"), file: "/film/journey-intro-v3", seconds: 26 },
  { id: "schedule", title: msg("Schedule an interview"), label: msg("Quick start"), file: "/film/schedule-interview-v5", seconds: 80 },
  { id: "role", title: msg("Create the role"), label: "1", file: "/film/journey-role-v3", seconds: 36 },
  { id: "screening", title: msg("Screen the resumes"), label: "2", file: "/film/journey-screening-v3", seconds: 36 },
  { id: "interviews", title: msg("Follow the interviews"), label: "3", file: "/film/journey-interviews-v3", seconds: 26 },
  { id: "report", title: msg("Read the report"), label: "4", file: "/film/journey-report-v3", seconds: 23 },
  { id: "review", title: msg("Compare and decide"), label: "5", file: "/film/journey-review-v4", seconds: 34 },
  { id: "extras", title: msg("Career page and talent pool"), label: "6", file: "/film/journey-extras-v3", seconds: 41 },
] as const;
export type FilmId = (typeof FILMS)[number]["id"];
type Cut = { file: string; seconds: number };

// Each portal language has its own cut of every film, recorded with the portal in that
// language and narrated in it (FILM_LANG in video/onboarding-journey). A language
// without a cut of a film plays the English one.
const LOCAL_CUTS: Record<string, Partial<Record<FilmId, Cut>>> = {
  de: {
    intro: { file: "/film/de/journey-intro-v1", seconds: 30 },
    schedule: { file: "/film/de/schedule-interview-v1", seconds: 94 },
    role: { file: "/film/de/journey-role-v1", seconds: 47 },
    screening: { file: "/film/de/journey-screening-v1", seconds: 47 },
    interviews: { file: "/film/de/journey-interviews-v1", seconds: 27 },
    report: { file: "/film/de/journey-report-v1", seconds: 26 },
    review: { file: "/film/de/journey-review-v1", seconds: 37 },
    extras: { file: "/film/de/journey-extras-v1", seconds: 41 },
  },
  fr: {
    intro: { file: "/film/fr/journey-intro-v1", seconds: 30 },
    schedule: { file: "/film/fr/schedule-interview-v1", seconds: 89 },
    role: { file: "/film/fr/journey-role-v1", seconds: 44 },
    screening: { file: "/film/fr/journey-screening-v1", seconds: 40 },
    interviews: { file: "/film/fr/journey-interviews-v1", seconds: 28 },
    report: { file: "/film/fr/journey-report-v1", seconds: 24 },
    review: { file: "/film/fr/journey-review-v1", seconds: 37 },
    extras: { file: "/film/fr/journey-extras-v1", seconds: 40 },
  },
  es: {
    intro: { file: "/film/es/journey-intro-v1", seconds: 33 },
    schedule: { file: "/film/es/schedule-interview-v1", seconds: 96 },
    role: { file: "/film/es/journey-role-v1", seconds: 51 },
    screening: { file: "/film/es/journey-screening-v1", seconds: 41 },
    interviews: { file: "/film/es/journey-interviews-v1", seconds: 29 },
    report: { file: "/film/es/journey-report-v1", seconds: 25 },
    review: { file: "/film/es/journey-review-v1", seconds: 37 },
    extras: { file: "/film/es/journey-extras-v1", seconds: 41 },
  },
  sv: {
    intro: { file: "/film/sv/journey-intro-v1", seconds: 31 },
    schedule: { file: "/film/sv/schedule-interview-v1", seconds: 98 },
    role: { file: "/film/sv/journey-role-v1", seconds: 47 },
    screening: { file: "/film/sv/journey-screening-v1", seconds: 42 },
    interviews: { file: "/film/sv/journey-interviews-v1", seconds: 29 },
    report: { file: "/film/sv/journey-report-v1", seconds: 27 },
    review: { file: "/film/sv/journey-review-v1", seconds: 36 },
    extras: { file: "/film/sv/journey-extras-v1", seconds: 43 },
  },
  it: {
    intro: { file: "/film/it/journey-intro-v1", seconds: 31 },
    schedule: { file: "/film/it/schedule-interview-v1", seconds: 96 },
    role: { file: "/film/it/journey-role-v1", seconds: 48 },
    screening: { file: "/film/it/journey-screening-v1", seconds: 41 },
    interviews: { file: "/film/it/journey-interviews-v1", seconds: 28 },
    report: { file: "/film/it/journey-report-v1", seconds: 27 },
    review: { file: "/film/it/journey-review-v1", seconds: 38 },
    extras: { file: "/film/it/journey-extras-v1", seconds: 42 },
  },
};

export function filmCut(film: (typeof FILMS)[number], language: string): Cut {
  return LOCAL_CUTS[language]?.[film.id] ?? film;
}

// The captions track: the cut's own language, English otherwise.
export function captionLanguage(film: (typeof FILMS)[number], language: string) {
  const code = LOCAL_CUTS[language]?.[film.id] ? language : "en";
  return { srcLang: code, label: UI_LANGUAGES.find((l) => l.code === code)?.name ?? "English" };
}

const minutes = (seconds: number) => Math.max(1, Math.round(seconds / 60));

interface ScheduleInterviewFilmProps {
  // "thumbnail" is the poster with a play badge (greeting, first-run wizard);
  // "row" is a one-line play button for the left panel.
  variant?: "thumbnail" | "row";
  // Where the play came from, for the product event.
  placement?: string;
}

export default function ScheduleInterviewFilm({ variant = "thumbnail", placement = variant }: ScheduleInterviewFilmProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const watched = useWatchedFilms();
  const [current, setCurrent] = useState<FilmId>("intro");
  // Set by the click that opens the player; from then on each film plays as soon as
  // its element mounts (the click is the user's go-ahead for sound).
  const [armed, setArmed] = useState(false);
  const { track } = useAnalytics();

  useEffect(() => {
    void loadWatched();
  }, []);

  const film = FILMS.find((f) => f.id === current) ?? FILMS[0];
  const index = FILMS.findIndex((f) => f.id === film.id);
  const seen = (id: FilmId) => watched.includes(id);
  const seenCount = FILMS.filter((f) => seen(f.id)).length;
  const next = FILMS.find((f) => !seen(f.id)) ?? null; // where to pick up
  const secondsLeft = FILMS.filter((f) => !seen(f.id)).reduce((s, f) => s + filmCut(f, language).seconds, 0);
  const cut = filmCut(film, language);
  const captions = captionLanguage(film, language);
  const allSeen = next === null;

  // Each film is its own <video> (keyed), so switching never fights React over src.
  const play = (id: FilmId) => {
    setArmed(true);
    if (id === current) {
      const video = videoRef.current;
      if (video) {
        if (video.ended) video.currentTime = 0;
        void video.play().catch(() => {});
      }
      return;
    }
    setCurrent(id);
  };

  // Opens where the recruiter left off (or at the intro, or the first film if all seen).
  const open = () => {
    if (!dialogRef.current) return;
    dialogRef.current.showModal();
    play((next ?? FILMS[0]).id);
  };

  const onPlaying = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    if (event.currentTarget.currentTime < 0.5) track(EVENTS.ONBOARDING_FILM_PLAY, { film: film.id, placement, language: captions.srcLang, cut: cut.file });
  };

  const onEnded = () => {
    track(EVENTS.ONBOARDING_FILM_COMPLETE, { film: film.id, placement, language: captions.srcLang, cut: cut.file });
    markWatched(film.id);
    // the films run on, one after the other
    const following = FILMS[index + 1];
    if (following) setCurrent(following.id);
  };

  const player = (
    <dialog
      ref={dialogRef}
      aria-label={t("How it works")}
      className="film-player w-[min(92vw,1100px)] rounded-2xl bg-black p-0 text-white shadow-2xl backdrop:bg-black/70"
      onClose={() => videoRef.current?.pause()}
      onClick={(event) => {
        if (event.target === event.currentTarget) event.currentTarget.close();
      }}
      // Escape closes the player (the dialog's own cancel) and must stop there: Nova
      // listens for Escape on the document and would dismiss its greeting underneath.
      onKeyDown={(event) => {
        if (event.key === "Escape") event.stopPropagation();
      }}
    >
      <form method="dialog" className="absolute right-3 top-3 z-10">
        <button
          aria-label={t("Close the walkthrough")}
          className="rounded-full bg-white/90 p-1.5 text-gray-800 hover:bg-white"
          type="submit"
        >
          <X className="h-4 w-4" />
        </button>
      </form>
      <video
        key={cut.file}
        ref={videoRef}
        autoPlay={armed}
        className="aspect-video w-full rounded-t-2xl"
        controls
        playsInline
        poster={`${cut.file}.jpg`}
        preload="metadata"
        src={`${cut.file}.mp4`}
        onPlaying={onPlaying}
        onEnded={onEnded}
      >
        {/* The narration carries the words; captions are there for whoever turns them on. */}
        <track kind="captions" label={captions.label} src={`${cut.file}.vtt`} srcLang={captions.srcLang} />
      </video>

      {/* Progress: how much of the onboarding has been seen, and what is left. */}
      <div className="px-4 pt-3" data-testid="onboarding-progress">
        <div className="flex items-center justify-between text-xs">
          <span aria-live="polite">
            {allSeen ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-300">
                <Check className="h-3.5 w-3.5" /> {t("You have seen it all. Replay anything below.")}
              </span>
            ) : (
              <>
                <span className="font-medium">{t("{{seen}} of {{total}} watched", { seen: seenCount, total: FILMS.length })}</span>
                <span className="text-white/60"> · {t("about {{minutes}} min left", { minutes: minutes(secondsLeft) })}</span>
              </>
            )}
          </span>
          {!allSeen && next && next.id !== film.id && (
            <button className="text-white/80 underline-offset-2 hover:underline" onClick={() => play(next.id)} type="button">
              {t("Up next: {{title}}", { title: t(next.title) })}
            </button>
          )}
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/15" role="progressbar" aria-valuemin={0} aria-valuemax={FILMS.length} aria-valuenow={seenCount} aria-label={t("Onboarding films watched")}>
          <div
            className="h-full rounded-full bg-emerald-400 motion-safe:transition-[width] motion-safe:duration-500"
            style={{ width: `${(seenCount / FILMS.length) * 100}%` }}
          />
        </div>
      </div>

      <nav aria-label={t("Chapters")} className="flex flex-wrap gap-1.5 px-3 py-2.5">
        {FILMS.map((f) => {
          const isCurrent = f.id === film.id;
          const isSeen = seen(f.id);
          return (
            <button
              key={f.id}
              aria-current={isCurrent ? "true" : undefined}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                isCurrent ? "bg-white text-black" : "bg-white/10 text-white hover:bg-white/20"
              }`}
              data-watched={isSeen ? "true" : undefined}
              onClick={() => play(f.id)}
              type="button"
            >
              {isSeen ? (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-400 text-black motion-safe:animate-in motion-safe:zoom-in-50" aria-label={t("watched")}>
                  <Check className="h-3 w-3" />
                </span>
              ) : (
                <span className="opacity-70">{t(f.label)}</span>
              )}
              {t(f.title)}
              {!isSeen && f.id === next?.id && !isCurrent && <span className="text-white/50">· {t("up next")}</span>}
            </button>
          );
        })}
      </nav>
    </dialog>
  );

  const control =
    variant === "row" ? (
      <button
        aria-haspopup="dialog"
        className="film-row flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/70 hover:text-primary"
        data-analytics-id="sidebar_schedule_film"
        onClick={open}
        type="button"
      >
        <span className={`film-row-mark flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white ${allSeen ? "bg-emerald-500" : "bg-[#4648D4]"}`}>
          {allSeen ? <Check className="h-3 w-3" /> : <Play className="ml-0.5 h-3 w-3 fill-current" />}
        </span>
        <span className="min-w-0 flex-1 leading-snug">{t("How it works")}</span>
        <span className="shrink-0 text-xs text-muted-foreground" data-testid="sidebar-film-progress">
          {allSeen ? t("Done") : seenCount ? `${seenCount}/${FILMS.length}` : t("{{total}} films", { total: FILMS.length })}
        </span>
      </button>
    ) : (
      <button
        aria-haspopup="dialog"
        className="group relative block w-full overflow-hidden rounded-xl border border-border text-left"
        data-analytics-id="onboarding_schedule_film"
        onClick={open}
        type="button"
      >
        <img
          alt={t("Scheduling an interview in the recruiter portal")}
          className="aspect-video w-full object-cover"
          src={`${filmCut(FILMS[1], language).file}.jpg`}
        />
        <span className="absolute inset-0 flex items-center justify-center bg-black/25 transition-colors group-hover:bg-black/35">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-[--rx-ink]">
            <Play className="ml-0.5 h-5 w-5 fill-current" />
          </span>
        </span>
        <span className="absolute bottom-2 left-2 rounded-md bg-black/70 px-2.5 py-1 text-xs font-medium text-white">
          {seenCount === 0 ? t("Watch · {{total}} short films", { total: FILMS.length }) : allSeen ? t("Watch again") : t("Continue · {{title}}", { title: next ? t(next.title) : "" })}
        </span>
      </button>
    );

  return (
    <>
      {control}
      {typeof document !== "undefined" && createPortal(player, document.body)}
    </>
  );
}
